import React, {createContext, useContext, useState, useEffect, useMemo} from 'react';
import {toast} from 'react-hot-toast';
import useLocalStorage from '../hooks/useLocalStorage';
import lang from "../JSON-FILES/lang.json"

const Context = createContext();

/**
 * #context
 * StateContext is a context that provides the state of the application.
 * It is responsible for storing the state of the application and providing it to the components.
 * It is also responsible for providing the functions to update the state.
 * 
 * The state of the application includes the following:
 * - showCart: a boolean that indicates whether the cart should be shown or not
 * - cartItems: an array of objects that represent the items in the cart
 * - totalPrice: a number that represents the total price of the items in the cart
 * - totalQuantities: a number that represents the total quantity of the items in the cart
 * - qty: a number that represents the quantity of the item to be added to the cart
 * - incQty: a function that increases the quantity of the item to be added to the cart
 * - decQty: a function that decreases the quantity of the item to be added to the cart
 * - onAdd: a function that adds a product to the cart
 * - toggleCartItemQuanitity: a function that toggles the quantity of a product in the cart
 * - onRemove: a function that removes a product from the cart
 * - setCartItems: a function that sets the cart items
 * - setTotalPrice: a function that sets the total price of the items in the cart
 * - setTotalQuantities: a function that sets the total quantity of the items in the cart
 * - customer: an object that represents the customer
 * - setCustomer: a function that sets the customer
 * - showLowbar: a boolean that indicates whether the lowbar should be shown or not
 * - setShowLowbar: a function that sets whether the lowbar should be shown or not
 * - searchTerm: a string that represents the search term
 * - setSearchTerm: a function that sets the search term
 * - client: an object that represents the client
 * - setClient: a function that sets the client
 * - site: an object that represents the site
 * - setSite: a function that sets the site
 * - register: an object that represents the register
 * - setRegister: a function that sets the register
 * - registerTemplates: an object that represents the register templates
 * - setRegisterTemplates: a function that sets the register templates
 * - clients: an array of objects that represent the clients
 * - setClients: a function that sets the clients
 * - sites: an array of objects that represent the sites
 * - setSites: a function that sets the sites
 * - registers: an array of objects that represent the registers
 * - setRegisters: a function that sets the registers
 * - currentTemplate: an object that represents the current template
 * - setCurrentTemplate: a function that sets the current template
 * - currentDevice: an object that represents the current device
 * - setCurrentDevice: a function that sets the current device
 * - currentNonConformity: an object that represents the current non conformity
 * - setCurrentNonConformity: a function that sets the current non conformity
 * - devices: an array of objects that represent the devices
 * - setDevices: a function that sets the devices
 * - deviceEdit: a boolean that indicates whether the device should be edited or not
 * - setDeviceEdit: a function that sets whether the device should be edited or not
 * - modalOpen: a boolean that indicates whether the modal should be open or not
 * - setModalOpen: a function that sets whether the modal should be open or not
 * - modalContent: an object that represents the content of the modal
 * - setModalContent: a function that sets the content of the modal
 * - sUser: an object that represents the user
 * - setSUser: a function that sets the user
 * - selectorVis: a boolean that indicates whether the selector should be visible or not
 * - setSelectorVis: a function that sets whether the selector should be visible or not
 * - nonConformities: an array of objects that represent the non conformities
 * - setNonConformities: a function that sets the non conformities
 * - customerUsers: an array of objects that represent the customer users
 * - setCustomerUsers: a function that sets the customer users
 * - duplicateDevice: a boolean that indicates whether the device should be duplicated or not
 * - setDuplicateDevice: a function that sets whether the device should be duplicated or not
 * - indexPage: a string that represents the index page
 * - setIndexPage: a function that sets the index page
 * - loading: a boolean that indicates whether the application is loading or not
 * - setLoading: a function that sets whether the application is loading or not
 * - deviceNonConformities: an array of objects that represent the device non conformities
 * - setDeviceNonConformities: a function that sets the device non conformities
 * - substituteDeviceId: a string that represents the substitute device id
 * - setsubstituteDeviceId: a function that sets the substitute device id
 * - deviceIndexPage: a string that represents the device index page
 * - setDeviceIndexPage: a function that sets the device index page
 * - LDB: a function that returns the label of a page
 * - mobileView: a boolean that indicates whether the mobile view should be shown or not
 * - setMobileView: a function that sets whether the mobile view should be shown or not
 * - screenPosition: a number that represents the screen position
 * - setScreenPosition: a function that sets the screen position
 * - subReasons: an array of objects that represent the sub reasons
 * - setSubReasons: a function that sets the sub reasons
 * - offer: an object that represents the offer
 * - setOffer: a function that sets the offer
 * - isEditingOffer: a boolean that indicates whether the offer is being edited or not
 * - setIsEditingOffer: a function that sets whether the offer is being edited or not
 * - excelData: an array of objects that represent the excel data
 * - setExcelData: a function that sets the excel data
 * - forceReload: a function that forces the reload of the application
 * - forceLoad: a boolean that indicates whether the application should be reloaded or not
 * - bypassNewPassword: a boolean that indicates whether the new password should be bypassed or not
 * - setBypassNewPassword: a function that sets whether the new password should be bypassed or not
 * - hideTestButtons: a boolean that indicates whether the test buttons should be hidden or not
  */
export const StateContext = ({children}) => {


    const hideTestButtons = false


    const [showCart, setShowCart] = useState(false);
    const [showLowbar, setShowLowbar] = useState(false);
    const [cartItems, setCartItems] = useState([]);
    const [totalPrice, setTotalPrice] = useState(0);
    const [totalQuantities, setTotalQuantities] = useState(0);
    const [qty, setQty] = useState(1);

    const [customer, setCustomer] = useLocalStorage("customer")


    const [client, setClient] = useLocalStorage("client")
    const [offer, setOffer] = useLocalStorage("offer")
    const [clientOrder, setClientOrder] = useLocalStorage("clientOrder")
    const [isEditingOffer, setIsEditingOffer] = useLocalStorage("isEditingOffer")
    const [site, setSite] = useLocalStorage("site")
    const [register, setRegister] = useLocalStorage("register")
    const [duplicateDevice, setDuplicateDevice] = useLocalStorage("duplicateDevice")
    const [subReasons, setSubReasons] = useLocalStorage("subReasons")
    const [deviceEdit, setDeviceEdit] = useState(false)
    const [loading, setLoading] = useState(false);

    const [customerUsers, setCustomerUsers] = useLocalStorage("customerUsers")
    const [registerTemplates, setRegisterTemplates] = useLocalStorage("registerTemplates");
    const [nonConformities, setNonConformities] = useLocalStorage("nonConformities");
    const [clients, setClients] = useLocalStorage("clients");
    const [sites, setSites] = useLocalStorage("sites");
    const [registers, setRegisters] = useLocalStorage("registers");
    const [devices, setDevices] = useLocalStorage("devices");
    const [mobileView, setMobileView] = useLocalStorage("mobileView");
    const [manualOffline, setManualOffline] = useLocalStorage("manualOffline");
    //const [offlineData, setOfflineData] = useLocalStorage("offlineData");
    const [isWorksheet, setIsWorksheet] = useLocalStorage("worksheet")
    
    const [deviceNonConformities, setDeviceNonConformities] = useLocalStorage("deviceNonConformities");

    const [currentTemplate, setCurrentTemplate] = useLocalStorage("currentTemplate");
    const [currentDevice, setCurrentDevice] = useLocalStorage("currentDevice");

    const [currentNonConformity, setCurrentNonConformity] = useState({});
    const [selectorVis, setSelectorVis] = useLocalStorage("selectorVis")

    const [excelData, setExcelData] = useLocalStorage("excelData");

    const [modalOpen, setModalOpen] = useState(false);
    const [forceLoad, setForceLoad] = useState(false);
    const [screenPosition, setScreenPosition] = useState(0);
    const [dropdownScreenPosition, setDropdownScreenPosition] = useState(0);
    const [modalContent, setModalContent] = useState(undefined);

    const [substituteDeviceId, setsubstituteDeviceId] = useState();

    const [sUser, setSUser] = useLocalStorage("user")
    const [bypassNewPassword, setBypassNewPassword] = useLocalStorage("bypassNewPassword")

    const LDB = (page, key) => {
        const sUserLanguage = !sUser?.language ? "it" : sUser?.language
        if(!key) return ""
        if(!page) return ""
        const langPage = lang[`${page}`]
        if(!langPage) return ""
        if(langPage[`${key}`]?.[`${sUserLanguage}`] == undefined) return ""

        return langPage[`${key}`][`${sUserLanguage}`]
    }

    const [searchTerm, setSearchTerm] = useState("");
    const [indexPage, setIndexPage] = useState("HOME");
    const [deviceIndexPage, setDeviceIndexPage] = useState("DEVICE")

    let foundProduct;
    let index;

    /** This function allows to add products to the cart
     * @param product
     * @param quantity
     *
    */
    const onAdd = (product, quantity) => {
        const checkProductInCart = !Array.isArray(cartItems) ? false : cartItems?.find?.((item) => item._id === product._id);

        setTotalPrice((prevTotalPrice) => prevTotalPrice + product.price * quantity);
        setTotalQuantities((prevTotalQuantities) => prevTotalQuantities + quantity);

        if (checkProductInCart) {
            const updatedCartItems = cartItems.map?.((cartProduct) => {
                if (cartProduct._id === product._id) 
                    return {
                        ...cartProduct,
                        quantity: cartProduct.quantity + quantity
                    }
                })

            setCartItems(updatedCartItems);
        } else {
            product.quantity = quantity;

            setCartItems([
                ...cartItems, {
                    ...product
                }
            ]);
        }

        toast.success(`${qty} ${product.name} added to the cart.`);
    }
    /** removes the product from the cart
   * @param product
  */

    const onRemove = (product) => {
        foundProduct = !Array.isArray(cartItems) ? undefined : cartItems?.find?.((item) => item._id === product._id);

        const newCartItems =  !Array.isArray(cartItems) ? [] : cartItems?.filter?.((item) => item._id !== product._id);

        setTotalPrice(
            (prevTotalPrice) => prevTotalPrice - foundProduct.price * foundProduct.quantity
        );
        setTotalQuantities(
            prevTotalQuantities => prevTotalQuantities - foundProduct.quantity
        );
        setCartItems(newCartItems);
    }

    /** This function allows the change of quantities from the cart
     * @param id
     * @param value
     * if value is 'inc' it increases, if value is 'dec' it decreases
    */
    const toggleCartItemQuanitity = (id, value) => {
        foundProduct = !Array.isArray(cartItems) ? undefined : cartItems?.find?.((item) => item._id === id)
        index = cartItems?.findIndex((product) => product._id === id);

        //const newCartItems = cartItems.filter?.((item) => item._id !== id)
        const newCartItems = [...cartItems];

        if (value === 'inc') {
            // setCartItems([...newCartItems, { ...foundProduct, quantity:
            // foundProduct.quantity + 1 } ]);
            foundProduct.quantity += 1;
            newCartItems[index] = foundProduct;
            setCartItems(newCartItems)

            setTotalPrice((prevTotalPrice) => prevTotalPrice + foundProduct.price)
            setTotalQuantities(prevTotalQuantities => prevTotalQuantities + 1)
        } else if (value === 'dec') {
            if (foundProduct.quantity > 1) {
                // setCartItems([...newCartItems, { ...foundProduct, quantity:
                // foundProduct.quantity - 1 } ]);
                foundProduct.quantity -= 1;
                newCartItems[index] = foundProduct
                setCartItems(newCartItems)

                setTotalPrice((prevTotalPrice) => prevTotalPrice - foundProduct.price)
                setTotalQuantities(prevTotalQuantities => prevTotalQuantities - 1)
            }
        }
    }
    /** This function increases the quantity of products */

    const incQty = () => {
        setQty((prevQty) => prevQty + 1);
    }

    /** This function decreaseases the quantity of products */
    const decQty = () => {
        setQty((prevQty) => {
            if (prevQty - 1 < 1) 
                return 1;
            
            return prevQty - 1;
        });
    }

    const forceReload = () => {
        setForceLoad(!forceLoad)
        console.log("force reload", !forceLoad)
    }

const apiUrl = useMemo( () => {
    const env = process.env.NODE_ENV
    let domain;
    if (typeof window !== 'undefined') {
      domain = window.location.origin;
    } else {
      domain = undefined;
    }
    if(env == "development"){
        //console.log("TEST ENV DEV", env, domain)
        // For development, always use localhost:8080 as the API URL
        return "http://localhost:8080"
    }
    else if (env == "production"){
        //console.log("TEST ENV PROD", env, domain)
        if(domain?.includes('beta.gssmega.app')){
            return "https://beta-api.gssmega.app"
        }
        return "https://apis.gssmega.app"
    }
    return "http://localhost:8080" // fallback
}, [] )  

    return (
        <Context.Provider
            value={{
                showCart,
                setShowCart,
                apiUrl,
                cartItems,
                totalPrice,
                totalQuantities,
                qty,
                incQty,
                decQty,
                onAdd,
                toggleCartItemQuanitity,
                onRemove,
                setCartItems,
                setTotalPrice,
                setTotalQuantities,
                customer,
                setCustomer,
                showLowbar, 
                setShowLowbar,
                searchTerm,
                setSearchTerm,
                client,
                setClient,
                site,
                setSite,
                register,
                setRegister,
                //device,
                //setDevice,
                registerTemplates,
                setRegisterTemplates,
                clients, 
                setClients,
                sites, 
                setSites,
                registers, 
                setRegisters,
                currentTemplate,
                setCurrentTemplate,
                currentDevice,
                setCurrentDevice,
                currentNonConformity, 
                setCurrentNonConformity,
                devices, 
                setDevices,
                deviceEdit, 
                setDeviceEdit,
                modalOpen, 
                setModalOpen,
                modalContent,
                setModalContent,
                sUser, 
                setSUser,
                selectorVis, 
                setSelectorVis,
                nonConformities,
                setNonConformities,
                customerUsers, 
                setCustomerUsers,
                duplicateDevice,
                setDuplicateDevice,
                indexPage, 
                setIndexPage,
                loading, 
                setLoading,
                deviceNonConformities,
                setDeviceNonConformities,
                substituteDeviceId,
                setsubstituteDeviceId,
                deviceIndexPage, 
                setDeviceIndexPage,
                LDB,
                mobileView, 
                setMobileView,
                screenPosition, 
                setScreenPosition,
                subReasons,
                setSubReasons,
                offer, 
                setOffer,
                isEditingOffer, 
                setIsEditingOffer,
                excelData,
                setExcelData,
                forceReload,
                forceLoad,
                bypassNewPassword, 
                setBypassNewPassword,
                dropdownScreenPosition, 
                setDropdownScreenPosition,
                clientOrder, 
                setClientOrder,
                isWorksheet, 
                setIsWorksheet,
                manualOffline, 
                setManualOffline,
                hideTestButtons
            }}>
            {children}
        </Context.Provider>
    )
}

export const useStateContext = () => useContext(Context);
